/** People Landing — campos del bloque "cards". Hasta el 21/09/2026 vivía en BlockEditorDialog.tsx. */
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { type LandingAlign } from '../../../../../../shared/types/people-landing';
import { type LandingCardItem } from '../../../../../../shared/types/people-landing';
import { ImageField } from '../ImageField';
import { AlignControl } from '../controls';
import type { EditorFieldProps } from '../types';

export function CardsFields({ draft, set, setItem, removeItem, moveItem, imageUrls, pageSlug, setDraft }: EditorFieldProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.section_title', 'Título de sección (opcional)')}</Label>
          <Input value={draft.title ?? ''} onChange={(e) => set({ title: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.align', 'Alineación')}</Label>
          <div>
            <AlignControl value={(draft.align ?? 'left') as LandingAlign} onChange={(a) => set({ align: a })} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.columns', 'Columnas')}</Label>
          <div className="flex gap-1.5">
            {[2, 3, 4].map((c) => (
              <Button
                key={c}
                type="button"
                size="sm"
                variant={draft.columns === c ? 'default' : 'outline'}
                onClick={() => set({ columns: c })}
              >
                {c}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {draft.items.map((item: LandingCardItem, idx: number) => (
          <div key={item.id ?? idx} className="rounded-lg border p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground uppercase">
                {t('people_landing.editor.card', 'Tarjeta')} {idx + 1}
              </p>
              <div className="flex gap-0.5">
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveItem(idx, 1)} disabled={idx === draft.items.length - 1}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeItem(idx)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <ImageField
              compact
              pageSlug={pageSlug}
              imageKey={item.imageKey}
              previewUrl={item.imageKey ? imageUrls[item.imageKey] : undefined}
              onChange={(k) => setItem(idx, { imageKey: k })}
            />
            <Input
              placeholder={t('people_landing.editor.card_title', 'Título')}
              value={item.title ?? ''}
              onChange={(e) => setItem(idx, { title: e.target.value })}
            />
            <Textarea
              placeholder={t('people_landing.editor.card_text', 'Texto (markdown)')}
              value={item.text ?? ''}
              onChange={(e) => setItem(idx, { text: e.target.value })}
              rows={3}
              className="text-[13px]"
            />
            <Input
              placeholder={t('people_landing.editor.card_url', 'Link (opcional)')}
              value={item.url ?? ''}
              onChange={(e) => setItem(idx, { url: e.target.value })}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            setDraft((d: any) => ({ ...d, items: [...d.items, { id: uuidv4() }] }))
          }
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {t('people_landing.editor.add_card', 'Agregar tarjeta')}
        </Button>
      </div>
    </>
  );
}
